import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MultiSelect } from '@/components/common/MultiSelect';
import PaginatedGentlyTable, {
  TableColumn,
  type PaginationConfig,
} from '@/components/common/PaginatedGentlyTable';
import { TableSkeleton } from '@/components/common/loading-skeletons/table-skeleton';
import { useExportTable } from '@/hooks/useExportTable';
import {
  useProductGroupsExport,
  downloadProductGroupsCsv,
} from '@/api/rebates/useRebateDataExports';
import type { ProductGroupExportItem } from '@erp/shared-types';
import { DownloadIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import useAllSuppliers from '@/api/accounts-payable/useAllSuppliersQuery';
// Note: ProductGroupsExport backend does not support agreementIds filter
// import useRebateAgreements from '@/api/rebates/useRebateAgreements';
import useCoreEnums from '@/api/core/core-enums/useCoreEnums';
import { REBATES_SYSTEM_ENUMS } from '@erp/shared-types';

const ProductGroupsExportTable: React.FC = () => {
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);

  const exportTable = useExportTable({ initialPageSize: 100 });

  const { data: allSuppliers } = useAllSuppliers();
  // Note: ProductGroupsExport backend does not support agreementIds filter
  // const { data: agreementsData } = useRebateAgreements({
  //   years: [],
  // });
  const { data: supplierTagsEnum } = useCoreEnums({
    name: REBATES_SYSTEM_ENUMS.SUPPLIER_TAGS.name,
    includeOptions: true,
  });
  const { data: productGroupTagsEnum } = useCoreEnums({
    name: REBATES_SYSTEM_ENUMS.PRODUCT_FAMILY_TAGS.name,
    includeOptions: true,
  });

  const supplierOptions = useMemo(() => {
    if (!allSuppliers) return [];
    return allSuppliers.map((supplier) => ({
      id: supplier.id,
      label: supplier.name,
      value: supplier.id,
    }));
  }, [allSuppliers]);

  // Note: ProductGroupsExport backend does not support agreementIds filter
  // const agreementOptions = useMemo(() => {
  //   if (!agreementsData) return [];
  //   return agreementsData.map((agreement) => ({
  //     id: agreement.id,
  //     label: agreement.name,
  //     value: agreement.id,
  //   }));
  // }, [agreementsData]);

  const supplierTagOptions = useMemo(() => {
    if (!supplierTagsEnum?.edges?.[0]?.options) return [];
    return supplierTagsEnum.edges[0].options.map((option) => ({
      id: option.id,
      label: option.name || '',
      value: option.id,
    }));
  }, [supplierTagsEnum]);

  const productGroupTagOptions = useMemo(() => {
    if (!productGroupTagsEnum?.edges?.[0]?.options) return [];
    return productGroupTagsEnum.edges[0].options.map((option) => ({
      id: option.id,
      label: option.name || '',
      value: option.id,
    }));
  }, [productGroupTagsEnum]);

  const apiFilters = useMemo(
    () => ({ ...exportTable.buildApiFilters(), includeCodes: true }),
    [exportTable],
  );
  const { data, isLoading, refetch } = useProductGroupsExport(
    apiFilters,
    shouldFetchData,
  );

  useEffect(() => {
    if (data?.pageInfo) {
      exportTable.pagination.setPaginationInfo({
        totalCount: data.pageInfo.totalCount,
        totalPages: data.pageInfo.totalPages,
        currentPage: data.pageInfo.currentPage,
      });
      exportTable.pagination.setHasNextPage(data.pageInfo.hasNextPage);
    }
  }, [data, exportTable.pagination]);

  const columns: TableColumn<ProductGroupExportItem>[] = useMemo(
    () => [
      { id: 'name', label: 'Product Group Name', sortable: true },
      { id: 'supplierName', label: 'Supplier', sortable: true },
      {
        id: 'supplierCode',
        label: 'Supplier Code',
        sortable: true,
        render: (val) => (val ? String(val) : '-'),
      },
      { id: 'description', label: 'Description', sortable: true },
      {
        id: 'tagName',
        label: 'Tag',
        sortable: true,
        render: (val) => (val ? String(val) : '-'),
      },
      {
        id: 'isHistorical',
        label: 'Historical',
        sortable: true,
        render: (val) => (val ? 'Yes' : 'No'),
      },
    ],
    [],
  );

  const paginationConfig: PaginationConfig | undefined = useMemo(() => {
    if (!shouldFetchData || !data?.pageInfo) return undefined;

    return {
      enabled: true,
      pagination: exportTable.pagination.paginationState,
      onPageSizeChange: exportTable.pagination.setPageSize,
      onPreviousPage: exportTable.pagination.goToPreviousPage,
      onNextPage: exportTable.pagination.goToNextPage,
      onFirstPage: exportTable.pagination.goToFirstPage,
      onLastPage: data.pageInfo.totalPages
        ? () => exportTable.pagination.goToPage(data.pageInfo.totalPages)
        : undefined,
      onGoToPage: exportTable.pagination.goToPage,
      showRowsPerPage: true,
      showPageInfo: true,
      showPageNumbers: true,
      rowsPerPageOptions: [10, 25, 50, 100, 200],
    };
  }, [shouldFetchData, data, exportTable.pagination]);

  const handleFetch = useCallback(() => {
    if (shouldFetchData) {
      refetch();
    } else {
      setShouldFetchData(true);
    }
  }, [shouldFetchData, refetch]);

  const handleDownloadCsv = useCallback(async () => {
    setIsDownloadingCsv(true);
    try {
      await downloadProductGroupsCsv(apiFilters);
    } catch (error) {
      console.error('Failed to download CSV:', error);
    } finally {
      setIsDownloadingCsv(false);
    }
  }, [apiFilters]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Product Groups Export</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <MultiSelect
              label="Suppliers"
              options={supplierOptions}
              value={exportTable.filters.suppliers}
              onChange={exportTable.setSuppliers}
              placeholder="Select suppliers..."
              searchPlaceholder="Search suppliers..."
            />
            <MultiSelect
              label="Supplier Tags"
              options={supplierTagOptions}
              value={exportTable.filters.supplierTags}
              onChange={exportTable.setSupplierTags}
              placeholder="Select tags..."
              searchPlaceholder="Search tags..."
            />
            <MultiSelect
              label="Product Group Tags"
              options={productGroupTagOptions}
              value={exportTable.filters.productGroupTags}
              onChange={exportTable.setProductGroupTags}
              placeholder="Select tags..."
              searchPlaceholder="Search tags..."
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              onClick={handleDownloadCsv}
              disabled={isDownloadingCsv}
              variant="outline"
            >
              <DownloadIcon className="h-4 w-4 mr-2" />
              {isDownloadingCsv ? 'Downloading...' : 'Download CSV'}
            </Button>
            <Button onClick={handleFetch} disabled={isLoading}>
              <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
              {isLoading ? 'Loading...' : 'Fetch Data'}
            </Button>
          </div>
        </div>

        {!shouldFetchData ? (
          <div className="text-center py-12 text-muted-foreground">
            Configure filters above and click "Fetch Data" to load results
          </div>
        ) : isLoading || !data?.items ? (
          <TableSkeleton />
        ) : (
          <PaginatedGentlyTable<ProductGroupExportItem>
            data={data.items}
            columns={columns}
            isLoading={isLoading}
            searchPlaceholder="Search product groups..."
            emptyMessage="No product groups found"
            showSearch={true}
            showDownload={false}
            downloadFilename="product-groups"
            showCount={true}
            itemName="product groups"
            paginationConfig={paginationConfig}
            serverSideSorting={{
              sortBy: exportTable.pagination.sortBy,
              sortDirection:
                exportTable.pagination.sortDirection === 'ASC' ? 'asc' : 'desc',
              onSort: (columnId, direction) => {
                exportTable.pagination.setSorting(
                  columnId,
                  direction === 'asc' ? 'ASC' : 'DESC',
                );
              },
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ProductGroupsExportTable;
